import React from 'react';
import { Card, CardContent } from '../components/ui/card';

export const Infrastructure = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1B4F72] to-[#138808] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Infrastructure</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Building the physical and digital backbone for India's mobility revolution
          </p>
        </div>
      </section>

      {/* Modern Mobility Infrastructure */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1B4F72] mb-6">Modern Mobility Infrastructure</h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Software alone cannot fix India's mobility challenges. Mahasarathi combines digital platforms with physical infrastructure to enable reliable, real-time transport systems across Bharat.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#1B4F72]/5 to-[#138808]/5 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-[#1B4F72] mb-4 text-center">Built for National Scale</h3>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto">
              From bus depots to smart interchanges, Mahasarathi is designed to operate in high-density metros and remote regions alike.
            </p>
          </div>

          {/* Four Pillars */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🏛️</div>
                <h4 className="text-lg font-bold text-[#1B4F72] mb-2">Public Infrastructure</h4>
                <p className="text-sm text-gray-600">Station-level systems that bring clarity and predictability to daily commuting</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🚌</div>
                <h4 className="text-lg font-bold text-[#1B4F72] mb-2">Vehicle Intelligence</h4>
                <p className="text-sm text-gray-600">IoT-enabled fleets providing real-time location, occupancy, and operational health</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🌏</div>
                <h4 className="text-lg font-bold text-[#1B4F72] mb-2">Last-Mile Networks</h4>
                <p className="text-sm text-gray-600">Inclusive transport models that ensure no region is left disconnected</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🚀</div>
                <h4 className="text-lg font-bold text-[#1B4F72] mb-2">Future-Ready</h4>
                <p className="text-sm text-gray-600">Infrastructure built not just for today, but for India's next 30 years</p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Infrastructure Sections */}
          <div className="space-y-16">
            {/* Smart Transport Hubs */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="text-5xl">🏛️</div>
                <div>
                  <h3 className="text-3xl font-bold text-[#1B4F72]">Smart Transport Hubs</h3>
                  <p className="text-gray-600">Airport-grade systems for everyday public transport</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Passenger Information Systems</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Live arrival and departure boards
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Multi-language announcements
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Disruption and delay alerts
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Secure & Seamless Access</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        QR / RFID-based ticket validation
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Biometric-ready infrastructure
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Fraud and misuse reduction
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Station Analytics</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Footfall and peak-hour analysis
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Queue and dwell-time monitoring
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Infrastructure utilization insights
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* IoT-Enabled Vehicles */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="text-5xl">🚌</div>
                <div>
                  <h3 className="text-3xl font-bold text-[#1B4F72]">IoT-Enabled Vehicles</h3>
                  <p className="text-gray-600">Turning every vehicle into a live data node</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Real-Time GPS & Telemetry</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Live vehicle tracking
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Schedule adherence monitoring
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Route deviation alerts
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Occupancy & Crowding Sensors</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Seat and standing capacity detection
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Overcrowding alerts
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Demand-based dispatch signals
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Operational Health</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Fuel and energy efficiency tracking
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Preventive maintenance indicators
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                        Reduced breakdowns and downtime
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Last-Mile & Regional Networks */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="text-5xl">🌏</div>
                <div>
                  <h3 className="text-3xl font-bold text-[#1B4F72]">Last-Mile & Regional Networks</h3>
                  <p className="text-gray-600">Designed for Bharat — not just metros</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Shared & Informal Transport</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Shared jeeps, e-autos, tempos
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Flexible routing models
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Integration with formal networks
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Cooperative Models</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Driver-owned and community-run fleets
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Fair revenue distribution
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Digital access without platform lock-in
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Low-Bandwidth Access</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        SMS / USSD-based journey information
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Offline-friendly ticketing flows
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                        Inclusion for non-smartphone users
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Future-Ready Mobility */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="text-5xl">🚀</div>
                <div>
                  <h3 className="text-3xl font-bold text-[#1B4F72]">Future-Ready Mobility</h3>
                  <p className="text-gray-600">Infrastructure built not just for today, but for India's next 30 years</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Autonomous Readiness</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Vehicle-to-infrastructure (V2I) support
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        High-resolution mapping inputs
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Policy-aligned experimentation zones
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Emerging Transport Modes</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Monorail and light rail systems
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Hyperloop and next-gen corridors
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Seamless integration into MaaS
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1B4F72] mb-4">Long-Term Planning</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        City and state mobility master plans
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Simulation and scenario modelling
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                        Data-backed policy decisions
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
